import {
  forwardRef,
  startTransition,
  useCallback,
  useContext,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';

import type {
  FilterChangedEvent,
  GridOptions,
  RowDoubleClickedEvent,
  SelectionChangedEvent,
} from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import { addToast } from '@/app/slices/toast';
import {
  GridStatusBar,
  type GridStatusBarParams,
  WithLocalStorage,
} from '@/components';
import { EditEmbryoDialog } from '@/components/common/edit-embryo-dialog';
import { EditEmbryoFormValues } from '@/components/common/edit-embryo-form/schema';
import { ToastLife } from '@/components/hoc/toast-container/enums';
import { useDefaultGridOptions } from '@/grid';
import { defaultColDef } from '@/grid/defaults';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import '@/pages/shipping-details/Shipment.css';
import { ShipmentStatus } from '@/pages/shipping/utils/enums';
import { Embryo } from '@/rtk-query.gql';
import { PrimaryContext } from '@/services';
import { EditEmbryoInput, useEditEmbryos } from '@/services/embryo-service';
import type { ShipmentEmbryo } from '@/services/shipping/types';

import { useEmbryoContextMenu } from '../../hooks/useEmbryoContextMenu';
import { useShipmentContext } from '../../hooks/useShipmentContext';
import { useEmbryoColumnDefs } from '../../utils/columnDefs';

const toOptionalIsoString = (
  value?: Date | string | null,
): string | undefined => {
  if (!value) {
    return undefined;
  }
  if (value instanceof Date) {
    return value.toISOString();
  }
  return new Date(value).toISOString();
};

const toOptionalNumber = (
  value?: number | string | null,
): number | undefined => {
  if (value === undefined || value === null || value === '') {
    return undefined;
  }
  return typeof value === 'number' ? value : Number(value);
};

const mapFormValuesToInput = (
  values: EditEmbryoFormValues,
): EditEmbryoInput => ({
  embryoId: values.embryoId,
  status: values.status,
  freshFrozen: values.freshFrozen,
  ownerId: values.ownerId,
  soldTo: values.soldTo,
  strawDisclaimer: values.strawDisclaimer,
  strawNumber: toOptionalNumber(values.strawNumber),
  cane: values.cane,
  stage: values.stage,
  grade: values.grade,
  storageLocationId: values.storageLocationId,
  siteId: values.siteId,
  shipmentId: values.shipmentId,
  creation: toOptionalIsoString(values.creation),
  stateCode: values.stateCode,
  statusCode: values.statusCode,
  lastAct: values.lastAct,
  donorId: values.donorId,
  sireId: values.sireId,
  embTag: values.embTag,
  sex: values.sex,
  breedId: values.breedId,
  freshStrawNo: values.freshStrawNo,
  freezeMethodId: values.freezeMethodId,
  freezer: values.freezer,
  dtPlus: values.dtPlus,
  tt: values.tt,
  freezeComment: values.freezeComment,
  export: values.export,
  freezeTechId: values.freezeTechId,
  recoveryFirmCode: values.recoveryFirmCode,
  freezeTime: values.freezeTime,
  receivedOn: toOptionalIsoString(values.receivedOn),
  grader7Id: values.grader7Id,
  crackedZona: values.crackedZona,
});

export type ShipmentEmbryoGridHandle = {
  deselectAll: () => void;
  openEditDialog: () => void;
};

type ShipmentEmbryoGridProps = {
  embryos: ShipmentEmbryo[] | null | undefined;
  selectedEmbryos: ShipmentEmbryo[];
  setSelectedEmbryos: (embryos: ShipmentEmbryo[]) => void;
  refetchEmbryos: () => Promise<unknown>;
  recaneEmbryoIds?: string[];
  multipleCanesEmbryoIds?: string[];
  onRemoveFromShipment: (embryos: ShipmentEmbryo[]) => void;
  canExecuteWorkflow: boolean;
  onEditEmbryo?: () => void;
};

export const ShipmentEmbryoGrid = forwardRef<
  ShipmentEmbryoGridHandle,
  ShipmentEmbryoGridProps
>(
  (
    {
      embryos,
      selectedEmbryos,
      setSelectedEmbryos,
      refetchEmbryos,
      recaneEmbryoIds,
      multipleCanesEmbryoIds,
      onRemoveFromShipment,
      canExecuteWorkflow,
      onEditEmbryo,
    },
    ref,
  ) => {
    const defaultGridOptions = useDefaultGridOptions();
    const isSelectFilteredEnabled = useFeatureFlag(
      'Shipping_Embryo_Select_Filtered',
    );

    useImperativeHandle(ref, () => ({
      deselectAll: () => {
        gridRef.current?.api?.deselectAll();
        setSelectedEmbryos([]);
      },
      openEditDialog: () => {
        setShowEditEmbryoDialog(true);
      },
    }));

    const columnDefs = useEmbryoColumnDefs(
      recaneEmbryoIds,
      embryos,
      multipleCanesEmbryoIds,
    );
    const dispatch = useDispatch();
    const gridRef = useRef<AgGridReact<ShipmentEmbryo>>(null);
    const gridReadyRef = useRef(false);
    const { themeMode } = useContext(PrimaryContext);
    const { shipment } = useShipmentContext();
    const { t } = useTranslation();
    const [showEditEmbryoDialog, setShowEditEmbryoDialog] = useState(false);

    const LOCKED_SHIPMENT_STATUSES = [
      ShipmentStatus.IN_PROGRESS as number,
      ShipmentStatus.SHIPPED as number,
    ];

    const isShipmentLocked = useMemo(() => {
      if (!shipment?.shipmentStatus) return false;
      return LOCKED_SHIPMENT_STATUSES.includes(shipment.shipmentStatus);
    }, [shipment?.shipmentStatus]);

    const readOnlyReason = useMemo(() => {
      if (!isShipmentLocked || !shipment?.shipmentStatus) return undefined;
      const statusName =
        shipment.shipmentStatus === (ShipmentStatus.SHIPPED as number)
          ? 'Shipped'
          : shipment.shipmentStatus === (ShipmentStatus.IN_PROGRESS as number)
            ? 'In Progress'
            : '';
      return t('embryo.embryoReadOnlyBanner', { status: statusName });
    }, [isShipmentLocked, shipment?.shipmentStatus, t]);

    const { getContextMenuItems } = useEmbryoContextMenu({
      selectedEmbryos,
      onRemoveFromShipment,
      canExecuteWorkflow,
      isRemoveFromShipmentDisabled: isShipmentLocked,
    });

    const { mutateAsync: editEmbryosMutation, isPending: isEditingEmbryo } =
      useEditEmbryos();

    const handleGridReady = useCallback(() => {
      gridReadyRef.current = true;
    }, []);

    const safeRefetchEmbryos = useCallback(async () => {
      if (!gridReadyRef.current || !gridRef.current?.api) {
        setTimeout(() => {
          startTransition(() => {
            refetchEmbryos();
          });
        }, 100);
        return;
      }
      startTransition(() => {
        refetchEmbryos();
      });
    }, [refetchEmbryos]);

    const handleSelectionChanged = useCallback(
      (event: SelectionChangedEvent<ShipmentEmbryo>) => {
        if (isSelectFilteredEnabled) {
          const filteredIds = new Set<string>();
          event.api.forEachNodeAfterFilter((node) => {
            if (node.data?.id) filteredIds.add(node.data.id);
          });
          const selectedRows = event.api
            .getSelectedRows()
            .filter((row) => filteredIds.has(row.id));
          setSelectedEmbryos(selectedRows);
        } else {
          setSelectedEmbryos(event.api.getSelectedRows());
        }
      },
      [setSelectedEmbryos, isSelectFilteredEnabled],
    );

    const handleFilterChanged = useCallback(
      (event: FilterChangedEvent<ShipmentEmbryo>) => {
        if (!isSelectFilteredEnabled) return;
        const filteredIds = new Set<string>();
        event.api.forEachNodeAfterFilter((node) => {
          if (node.data?.id) filteredIds.add(node.data.id);
        });
        const selectedRows = event.api
          .getSelectedRows()
          .filter((row) => filteredIds.has(row.id));
        setSelectedEmbryos(selectedRows);
      },
      [setSelectedEmbryos, isSelectFilteredEnabled],
    );

    const handleRowDoubleClicked = useCallback(
      (event: RowDoubleClickedEvent<ShipmentEmbryo>) => {
        if (onEditEmbryo) {
          setSelectedEmbryos([event.data as ShipmentEmbryo]);
          onEditEmbryo();
          return;
        }
        setShowEditEmbryoDialog(true);
        setSelectedEmbryos([event.data as ShipmentEmbryo]);
      },
      [selectedEmbryos, dispatch, setSelectedEmbryos, onEditEmbryo],
    );

    const handleSaveEmbryo = useCallback(
      async (data: EditEmbryoFormValues) => {
        try {
          const transformedInput = mapFormValuesToInput(data);
          const result = await editEmbryosMutation([transformedInput]);
          if (result.length > 0) {
            dispatch(
              addToast({
                severity: 'success',
                summary: 'Success',
                detail: `Successfully updated embryo record for ${result[0].data?.name}`,
                life: ToastLife.MEDIUM,
              }),
            );
          }

          await safeRefetchEmbryos();
          gridRef.current?.api?.deselectAll();
          setShowEditEmbryoDialog(false);
          setSelectedEmbryos([]);
        } catch (error) {
          dispatch(
            addToast({
              severity: 'error',
              summary: 'Error',
              detail: 'Failed to update storage location',
              life: ToastLife.MEDIUM,
            }),
          );
        }
      },
      [dispatch, editEmbryosMutation, safeRefetchEmbryos, selectedEmbryos],
    );

    const statusBar = useMemo<GridOptions['statusBar']>(
      () => ({
        statusPanels: [
          {
            statusPanel: GridStatusBar,
            statusPanelParams: {
              entityLabel: 'embryos',
            } satisfies GridStatusBarParams,
            align: 'left',
          },
        ],
      }),
      [],
    );

    return (
      <div className="shipment__grid ag-grid-height-300">
        <WithLocalStorage storageKey="shipment-embryo-grid">
          <AgGridReact<ShipmentEmbryo>
            gridOptions={defaultGridOptions}
            ref={gridRef}
            className={`ag-theme-transova${themeMode === 'dark' ? '-dark' : ''}`}
            getContextMenuItems={getContextMenuItems}
            defaultColDef={defaultColDef}
            rowData={embryos}
            columnDefs={columnDefs}
            rowSelection="multiple"
            animateRows
            onGridReady={handleGridReady}
            onSelectionChanged={handleSelectionChanged}
            onFilterChanged={handleFilterChanged}
            onRowDoubleClicked={handleRowDoubleClicked}
            statusBar={statusBar}
          />
        </WithLocalStorage>

        <EditEmbryoDialog
          visible={showEditEmbryoDialog}
          onHide={() => setShowEditEmbryoDialog(false)}
          onSave={handleSaveEmbryo}
          embryo={selectedEmbryos?.[0] as unknown as Embryo}
          readOnly={isShipmentLocked}
          readOnlyReason={readOnlyReason}
        />
      </div>
    );
  },
);
